import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/authContext';
import AuthImage from "../../components/AuthImage";

const CATEGORIAS = ['menaje', 'comida', 'limpieza', 'varios', 'aparcamiento'];
const TIPOS_LUGAR = ['piso', 'reuniones'];

const TIPO_LABEL = {
  piso: 'Piso',
  reuniones: 'Reuniones',
};

const CAT_COLORS = {
  menaje:       { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  comida:       { bg: '#fff7ed', border: '#f97316', text: '#c2410c' },
  limpieza:     { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
  varios:       { bg: '#f5f3ff', border: '#8b5cf6', text: '#5b21b6' },
  aparcamiento: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
};

const TIPO_COLORS = {
  piso:      { bg: '#eef2ff', border: '#6366f1', text: '#3730a3' },
  reuniones: { bg: '#fdf2f8', border: '#ec4899', text: '#9d174d' },
};

const ESTADO_COLOR = {
  gastado: '#f59e0b',
  liquidado: '#10b981',
};

const formatFecha = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatFechaCreacion = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  tipo_lugar: 'piso',
  id_piso: '',
  abreviatura_piso: '',
  sinPiso: false,
  categoria: 'menaje',
  importe: '',
  fecha_gasto: today(),
  notas: '',
  foto: null,
  fotoPreview: null,
});

const Prestamos = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const usuario = authUser?.user || authUser || '';

  const [gastos, setGastos] = useState([]);
  const [prestamos, setPrestamos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pisos, setPisos] = useState([]);
  const [bolsaActiva, setBolsaActiva] = useState(null);
  const [openBolsas, setOpenBolsas] = useState({});

  // Modal añadir/editar
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null); // gasto entero o null
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [errorForm, setErrorForm] = useState('');

  // Con el formulario abierto se oculta la barra de fichaje de abajo (tapa los botones)
  useEffect(() => {
    if (!showModal) return undefined;
    window.dispatchEvent(new CustomEvent('fichaje:ocultar', { detail: true }));
    return () => window.dispatchEvent(new CustomEvent('fichaje:ocultar', { detail: false }));
  }, [showModal]);

  // Ver foto ampliada
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  const fileInputRef = useRef(null);

  // Agrupar por bolsa
  const bolsas = React.useMemo(() => {
    const map = {};
    gastos.forEach((g) => {
      if (!map[g.n_bolsa]) map[g.n_bolsa] = [];
      map[g.n_bolsa].push(g);
    });
    return Object.entries(map)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([n, lista]) => ({
        n_bolsa: Number(n),
        gastos: lista,
        estado: lista.every((g) => g.estado === 'liquidado') ? 'liquidada' : 'activa',
        total: lista.reduce((s, g) => s + parseFloat(g.importe || 0), 0),
      }));
  }, [gastos]);

  useEffect(() => {
    if (!usuario) return;
    fetchGastos();
    fetchPisos();
    fetchBolsaActiva();
    fetchPrestamos();
  }, [usuario]);

  const fetchPrestamos = async () => {
    try {
      const r = await axios.get(`/api/prestamos/prestamos?user_supervisora=${encodeURIComponent(usuario)}`);
      setPrestamos(r.data.prestamos || []);
    } catch (e) {}
  };

  const fetchGastos = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/prestamos/gastos?user_supervisora=${encodeURIComponent(usuario)}`);
      setGastos(r.data.gastos || []);
    } catch (e) {
      console.error('Error cargando gastos', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBolsaActiva = async () => {
    try {
      const r = await axios.get(`/api/prestamos/gastos/bolsa_activa?user_supervisora=${encodeURIComponent(usuario)}`);
      setBolsaActiva(r.data.n_bolsa);
    } catch (e) {}
  };

  const fetchPisos = async () => {
    try {
      const r = await axios.get(`/api/pisoCrearCompras/pisos_supervisora_servicios?user=${encodeURIComponent(usuario)}`);
      const lista = Array.isArray(r.data) ? r.data : [];
      setPisos(lista);
    } catch (e) {
      console.error('Error cargando pisos', e);
    }
  };

  const handleAbrirAñadir = () => {
    setEditando(null);
    setForm(emptyForm());
    setErrorForm('');
    setShowModal(true);
  };

  const handleAbrirEditar = (gasto) => {
    if (gasto.estado === 'liquidado') {
      alert('Este gasto ya está liquidado y no puede editarse.');
      return;
    }
    setEditando(gasto);
    setForm({
      tipo_lugar: gasto.tipo_lugar,
      id_piso: gasto.id_piso || '',
      abreviatura_piso: gasto.abreviatura_piso || '',
      sinPiso: !!(gasto.tipo_lugar && !gasto.id_piso),
      categoria: gasto.categoria,
      importe: gasto.importe,
      fecha_gasto: gasto.fecha_gasto,
      notas: gasto.notas || '',
      foto: null,
      fotoPreview: gasto.foto_path ? `/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}` : null,
    });
    setErrorForm('');
    setShowModal(true);
  };

  const handleCerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setForm(emptyForm());
    setErrorForm('');
  };

  // Compress image before upload to avoid large payloads (mobile cameras)
  const compressImage = (file, maxWidth = 1280, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject(new Error('No file'));
      const reader = new FileReader();
      reader.onload = function(e) {
        const img = new Image();
        img.onload = function() {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, maxWidth / img.width);
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(blob => {
            if (!blob) return reject(new Error('Compression failed'));
            const newFile = new File([blob], (file.name || 'ticket.jpg').replace(/\s+/g,'_'), { type: 'image/jpeg' });
            resolve(newFile);
          }, 'image/jpeg', quality);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    let compressed = file;
    try { compressed = await compressImage(file, 1280, 0.7); } catch(_) {}
    setForm((f) => ({
      ...f,
      foto: compressed,
      fotoPreview: URL.createObjectURL(compressed),
    }));
  };

  const handleTipoLugarChange = (v) => {
    setForm((f) => ({
      ...f,
      tipo_lugar: v,
      id_piso: '',
      abreviatura_piso: '',
      sinPiso: false,
    }));
  };

  const handlePisoChange = (e) => {
    const id = e.target.value;
    const piso = pisos.find((p) => String(p.id) === String(id));
    setForm((f) => ({
      ...f,
      id_piso: id,
      abreviatura_piso: piso ? piso.abreviatura : '',
    }));
  };

  const handleToggleSinPiso = () => {
    setForm((f) => ({ ...f, sinPiso: !f.sinPiso, id_piso: '', abreviatura_piso: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorForm('');
    if (!form.importe || parseFloat(form.importe) <= 0) {
      setErrorForm('El importe debe ser mayor que 0');
      return;
    }
    if (!form.fecha_gasto) {
      setErrorForm('La fecha del gasto es obligatoria');
      return;
    }
    if (form.categoria !== 'aparcamiento' && !form.tipo_lugar) {
      setErrorForm('Selecciona un lugar');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('user_supervisora', usuario);
      fd.append('categoria', form.categoria);
      fd.append('importe', form.importe);
      fd.append('fecha_gasto', form.fecha_gasto);
      fd.append('notas', form.notas || '');
      if (form.categoria !== 'aparcamiento') {
        fd.append('tipo_lugar', form.tipo_lugar);
        fd.append('id_piso', form.id_piso);
        fd.append('abreviatura_piso', form.abreviatura_piso);
      }
      if (form.foto) {
        let fotoToUpload = form.foto;
        try { fotoToUpload = await compressImage(form.foto, 1280, 0.7); } catch(_) {}
        fd.append('foto', fotoToUpload);
      }

      if (editando) {
        await axios.put(`/api/prestamos/gastos/${editando.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post('/api/prestamos/gastos', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await fetchGastos();
      await fetchBolsaActiva();
      await fetchPrestamos();
      handleCerrarModal();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al guardar el gasto';
      setErrorForm(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEliminar = async (gasto) => {
    if (gasto.estado === 'liquidado') {
      alert('No puedes eliminar un gasto liquidado.');
      return;
    }
    if (!window.confirm(`¿Eliminar gasto de ${parseFloat(gasto.importe).toFixed(2)}€?`)) return;
    try {
      await axios.delete(`/api/prestamos/gastos/${gasto.id}`);
      await fetchGastos();
      await fetchBolsaActiva();
      await fetchPrestamos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const saldo = (() => {
    const totalPrestado = prestamos.reduce((s, p) => s + parseFloat(p.importe || 0), 0);
    const totalGastado = gastos.reduce((s, g) => s + parseFloat(g.importe || 0), 0);
    return totalPrestado - totalGastado;
  })();

  const toggleBolsa = (n) => setOpenBolsas((prev) => ({ ...prev, [n]: !prev[n] }));
  const isBolsaOpen = (bolsa) => {
    if (openBolsas[bolsa.n_bolsa] !== undefined) return openBolsas[bolsa.n_bolsa];
    return bolsa.estado === 'activa'; // activas abiertas por defecto, liquidadas cerradas
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <button onClick={() => navigate('/supervisora/compras')} style={s.btnVolver}>← Volver</button>
        <h2 style={s.titulo}>Gastos</h2>
        <div style={{ ...s.saldoBox, background: saldo >= 0 ? '#dcfce7' : '#fee2e2', color: saldo >= 0 ? '#15803d' : '#b91c1c' }}>
          {saldo >= 0 ? '+' : ''}{saldo.toFixed(2)} €
        </div>
      </div>

      {/* Bolsa activa info */}
      {bolsaActiva !== null ? (
        <div style={s.infoBar}>
          <span>Bolsa activa: <strong>#{bolsaActiva}</strong></span>
        </div>
      ) : (
        <div style={{ ...s.infoBar, background: '#f3f4f6', color: '#6b7280' }}>
          <span>No hay bolsa activa. El próximo gasto creará la bolsa #{(bolsas.length > 0 ? bolsas[0].n_bolsa : 0) + 1}</span>
        </div>
      )}

      {/* Lista de bolsas */}
      {loading ? (
        <p style={s.cargando}>Cargando gastos...</p>
      ) : bolsas.length === 0 ? (
        <p style={s.cargando}>Sin gastos registrados aún.</p>
      ) : (
        bolsas.map((bolsa) => {
          const open = isBolsaOpen(bolsa);
          return (
            <div key={bolsa.n_bolsa} style={s.bolsaCard}>
              <div style={s.bolsaHeader} onClick={() => toggleBolsa(bolsa.n_bolsa)}>
                <span style={s.bolsaTitulo}>Bolsa #{bolsa.n_bolsa}</span>
                <span style={{ ...s.estadoBadge, background: bolsa.estado === 'liquidada' ? '#10b981' : '#f59e0b' }}>
                  {bolsa.estado === 'liquidada' ? 'Liquidada' : 'Activa'}
                </span>
                <span style={s.bolsaTotal}>{bolsa.total.toFixed(2)} €</span>
                <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
              </div>
              {open && bolsa.gastos.map((g) => (
                <GastoRow
                  key={g.id}
                  gasto={g}
                  onEditar={handleAbrirEditar}
                  onEliminar={handleEliminar}
                  onVerFoto={setFotoAmpliada}
                />
              ))}
            </div>
          );
        })
      )}

      {/* Modal añadir/editar */}
      {showModal && (
        <div style={s.overlay} onClick={(e) => { if (e.target === e.currentTarget) handleCerrarModal(); }}>
          <div style={s.modal}>
            <h3 style={s.modalTitulo}>{editando ? 'Editar gasto' : 'Nuevo gasto'}</h3>
            <form onSubmit={handleSubmit} style={s.modalForm}>

              {/* Categoría */}
              <label style={s.label}>Categoría *</label>
              <div style={s.chipGroup}>
                {CATEGORIAS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, categoria: c, tipo_lugar: c === 'aparcamiento' ? '' : (f.tipo_lugar || 'piso'), id_piso: '', abreviatura_piso: '', sinPiso: false }))}
                    style={{ ...s.chip, ...(form.categoria === c ? { border: `1.5px solid ${CAT_COLORS[c].border}`, background: CAT_COLORS[c].bg, color: CAT_COLORS[c].text, fontWeight: 700 } : {}) }}
                  >
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>

              {/* Tipo lugar (solo si no es aparcamiento) */}
              {form.categoria !== 'aparcamiento' && (
                <>
                  <label style={s.label}>Lugar *</label>
                  <div style={s.chipGroup}>
                    {TIPOS_LUGAR.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => handleTipoLugarChange(t)}
                        style={{ ...s.chip, ...(form.tipo_lugar === t ? { border: `1.5px solid ${TIPO_COLORS[t].border}`, background: TIPO_COLORS[t].bg, color: TIPO_COLORS[t].text, fontWeight: 700 } : {}) }}
                      >
                        {TIPO_LABEL[t]}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Selector piso (para piso y reuniones) — opcional */}
              {form.categoria !== 'aparcamiento' && (form.tipo_lugar === 'piso' || form.tipo_lugar === 'reuniones') && (
                <>
                  <label style={s.label}>Piso</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select style={{ ...s.input, flex: 1 }} value={form.id_piso} onChange={handlePisoChange} disabled={form.sinPiso}>
                      <option value="">-- Seleccionar piso --</option>
                      {pisos.map((p) => (
                        <option key={p.id} value={p.id}>{p.abreviatura}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleToggleSinPiso}
                      style={{ ...s.chip, whiteSpace: 'nowrap', ...(form.sinPiso ? { border: '1.5px solid #6b7280', background: '#374151', color: '#fff', fontWeight: 700 } : {}) }}
                    >
                      {form.sinPiso ? '✅ Sin piso' : '📍 Sin piso'}
                    </button>
                  </div>
                </>
              )}

              {/* Importe */}
              <label style={s.label}>Importe (€) *</label>
              <input
                style={s.input}
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={form.importe}
                onChange={(e) => setForm((f) => ({ ...f, importe: e.target.value }))}
                required
              />

              {/* Fecha gasto */}
              <label style={s.label}>Fecha del gasto *</label>
              <input
                style={s.input}
                type="date"
                value={form.fecha_gasto}
                onChange={(e) => setForm((f) => ({ ...f, fecha_gasto: e.target.value }))}
                required
              />

              {/* Notas */}
              <label style={s.label}>Notas</label>
              <textarea
                style={{ ...s.input, height: 70, resize: 'vertical' }}
                placeholder="Observaciones (opcional)"
                value={form.notas}
                onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              />

              {/* Foto */}
              <label style={s.label}>Foto del ticket</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.fotoPreview && (
                  <AuthImage
                    src={form.fotoPreview}
                    alt="Vista previa"
                    style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'contain', border: '1px solid #e5e7eb' }}
                  />
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.setAttribute('capture', 'environment');
                      fileInputRef.current.click();
                    }
                  }} style={s.btnFoto}>📷 Cámara</button>
                  <button type="button" onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.removeAttribute('capture');
                      fileInputRef.current.click();
                    }
                  }} style={s.btnFoto}>🖼 Galería</button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFotoChange}
                />
              </div>

              {errorForm && <p style={s.error}>{errorForm}</p>}

              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button type="submit" style={s.btnGuardar} disabled={submitting}>
                  {submitting ? 'Guardando...' : (editando ? '💾 Guardar' : '➕ Añadir')}
                </button>
                <button type="button" onClick={handleCerrarModal} style={s.btnCancelar}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAB añadir gasto */}
      <button onClick={handleAbrirAñadir} style={s.fab} title="Añadir gasto">＋</button>

      {/* Lightbox foto */}
      {fotoAmpliada && (
        <div style={s.overlay} onClick={() => setFotoAmpliada(null)}>
          <div style={{ maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <AuthImage src={fotoAmpliada} alt="Ticket ampliado" style={{ maxWidth: '95vw', maxHeight: '80vh', borderRadius: 10, objectFit: 'contain' }} />
            <button
              onClick={() => setFotoAmpliada(null)}
              style={{ ...s.btnCancelar, alignSelf: 'center' }}
            >Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

const GastoRow = ({ gasto, onEditar, onEliminar, onVerFoto }) => {
  const [expanded, setExpanded] = useState(false);
  const liquidado = gasto.estado === 'liquidado';

  return (
    <div style={{ ...gs.row, opacity: liquidado ? 0.8 : 1, borderLeft: `4px solid ${(CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).border}` }}>
      <div style={gs.rowMain} onClick={() => setExpanded((v) => !v)}>
        <div style={gs.rowLeft}>
          <span style={{ ...gs.estadoPoint, background: ESTADO_COLOR[gasto.estado] }} />
          <div>
            <div style={{ ...gs.rowTipo, color: gasto.categoria === 'aparcamiento' ? CAT_COLORS.aparcamiento.text : ((TIPO_COLORS[gasto.tipo_lugar] || {}).text || '#111827') }}>
              {gasto.categoria === 'aparcamiento' ? 'Aparcamiento' : (TIPO_LABEL[gasto.tipo_lugar] || gasto.tipo_lugar || '')}{gasto.abreviatura_piso ? ` — ${gasto.abreviatura_piso}` : ''}
            </div>
            <div style={gs.rowCat}>
              <span style={{ display: 'inline-block', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700, background: (CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).bg, color: (CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).text, border: `1px solid ${(CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).border}` }}>{gasto.categoria}</span>
              {' · '}{formatFecha(gasto.fecha_gasto)}
            </div>
          </div>
        </div>
        <div style={gs.rowRight}>
          <span style={gs.importe}>{parseFloat(gasto.importe).toFixed(2)} €</span>
          <span style={gs.chevron}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={gs.detail}>
          <p style={gs.detailLine}><strong>ID:</strong> {gasto.id}</p>
          <p style={gs.detailLine}><strong>Estado:</strong> {gasto.estado}</p>
          <p style={gs.detailLine}><strong>Añadido:</strong> {formatFechaCreacion(gasto.fecha_creacion)}</p>
          {gasto.notas && <p style={gs.detailLine}><strong>Notas:</strong> {gasto.notas}</p>}
          {gasto.foto_path && (
            <div style={{ marginTop: 6 }}>
              <AuthImage
                src={`/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}`}
                alt="Ticket"
                style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'contain', cursor: 'pointer', border: '1px solid #e5e7eb' }}
                onClick={() => onVerFoto(`/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}`)}
              />
            </div>
          )}
          {!liquidado && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => onEditar(gasto)} style={gs.btnEdit}>✏️ Editar</button>
              <button onClick={() => onEliminar(gasto)} style={gs.btnDel}>⚠️ Eliminar</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const s = {
  page: { maxWidth: 600, margin: '0 auto', padding: '0 0 60px', fontFamily: "'Segoe UI', sans-serif", background: '#f9fafb', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 10px', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 40 },
  titulo: { fontSize: 18, fontWeight: 700, color: '#1e3a8a', margin: 0 },
  btnVolver: { background: 'none', border: 'none', color: '#3b82f6', fontWeight: 600, fontSize: 15, cursor: 'pointer', padding: '6px 0' },
  saldoBox: { borderRadius: 10, padding: '5px 12px', fontWeight: 800, fontSize: 15 },
  fab: { position: 'fixed', bottom: 'calc(24px + var(--fichaje-h, 0px))', right: 20, width: 56, height: 56, borderRadius: '50%', background: '#3b82f6', color: '#fff', border: 'none', fontSize: 28, cursor: 'pointer', boxShadow: '0 4px 16px rgba(59,130,246,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  infoBar: { background: '#dbeafe', color: '#1e40af', padding: '8px 16px', fontSize: 13, fontWeight: 500 },
  cargando: { textAlign: 'center', color: '#6b7280', marginTop: 40 },
  bolsaCard: { margin: '14px 12px 0', background: '#fff', borderRadius: 14, border: '1.5px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  bolsaHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '15px 18px', background: '#f0f4ff', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', userSelect: 'none' },
  bolsaTitulo: { fontWeight: 800, fontSize: 18, color: '#1e3a8a', flex: 1 },
  estadoBadge: { color: '#fff', borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 700 },
  bolsaTotal: { fontWeight: 800, fontSize: 18 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#fff', borderRadius: 14, padding: '20px 18px', width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto' },
  modalTitulo: { margin: '0 0 14px', fontSize: 17, fontWeight: 700, color: '#1e3a8a' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: 10 },
  label: { fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: -4 },
  input: { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box' },
  chipGroup: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: { border: '1.5px solid #d1d5db', borderRadius: 20, padding: '5px 14px', fontSize: 13, cursor: 'pointer', background: '#fff', fontWeight: 500 },
  chipActive: { border: '1.5px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700 },
  btnFoto: { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', background: '#f9fafb', fontWeight: 500 },
  btnGuardar: { flex: 1, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  btnCancelar: { flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '11px', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  error: { color: '#ef4444', fontSize: 13, margin: 0 },
};

const gs = {
  row: { borderBottom: '1px solid #f3f4f6' },
  rowMain: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', cursor: 'pointer' },
  rowLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  estadoPoint: { width: 13, height: 13, borderRadius: '50%', flexShrink: 0 },
  rowTipo: { fontWeight: 700, fontSize: 15, color: '#111827' },
  rowCat: { fontSize: 13, color: '#6b7280', marginTop: 3 },
  rowRight: { display: 'flex', alignItems: 'center', gap: 10 },
  importe: { fontWeight: 800, fontSize: 18 },
  chevron: { fontSize: 13, color: '#9ca3af' },
  detail: { padding: '10px 18px 16px 40px', background: '#fafafa' },
  detailLine: { fontSize: 14, color: '#374151', margin: '4px 0' },
  btnEdit: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontWeight: 700 },
  btnDel: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontWeight: 700 },
};

export default Prestamos;
